import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting.src';
import Draggable from 'react-draggable';
import { Button } from 'react-bootstrap';
import withChartExport from '../../../components/ChartExporting';

import highchartFactory from '../../../util/highcharts';
import chartUtils from '../../../util/chartUtils';
const highchartUtils = highchartFactory(Highcharts);

import { saveConfig, updateLineChart } from '../../../components/Filter/FilterActions';
import dateUtils from '../../../util/dates';

import expandIcon from '../../../assets/img/zoom-icon-off.svg';
import collapseIcon from '../../../assets/img/zoom-icon-on.svg';
import ChartDownloadMenu from '../../../components/ChartDownloadMenu';

const config = {
  chart: {
    type: 'spline',
    backgroundColor: '#fff'
  },
  exporting: {
    enabled: false
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  legend: {
    enabled: false
  },
  colors: ['#0086b3'],
  tooltip: {
    useHTML: true,
    formatter: function() {
      return `<span class="tooltip-value">${this.y} item${this.y != 1 ? 's' : ''}</span>`;
    }
  },
  xAxis: {
    type: 'datetime',
    gridLineWidth: 0,
    ordinal: false,
    showLastTickLabel: true,
    title: {
      text: null
    },
    plotLines: [
      {
        value: 0
      }
    ]
  },
  yAxis: {
    gridLineWidth: 0,
    labels: {
      enabled: false
    },
    title: {
      text: null
    },
    showFirstLabel: false
  },
  plotOptions: {
    spline: {
      color: '#7CB5EC',
      lineWidth: 1.5,
      marker: {
        enabledThreshold: 2,
        lineWidth: 1,
        radius: 5
      },
      states: {
        hover: {
          lineWidth: 2,
          halo: {
            opacity: 0
          },
          marker: {
            enabled: true
          }
        }
      }
    }
  },
  series: [
    {
      marker: {
        symbol: 'circle'
      },
      name: 'Throughput',
      data: []
    }
  ]
};

const DEFAULT_POS = { x: 95, y: 85 };

export class ExactThroughput extends React.Component {
  constructor(props) {
    super(props);
    this.chartContainer = React.createRef();

    this.state = {
      total: 0,
      isFullSreen: false,
      width: undefined,
      height: undefined,
      pos: DEFAULT_POS
    };
  }

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    config.exporting = { ...config.exporting, ...this.props.chartExporting };

    this.chart = new Highcharts['Chart'](this.chartContainer.current, config);

    if (this.props.data && this.props.data.length) {
      this.updateDateFormat(this.props.dateFormat);
      this.calculateChartData();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data != this.props.data || prevProps.dateFormat != this.props.dateFormat) {
      this.calculateChartData();
      this.updateDateFormat(this.props.dateFormat);
    }

    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateDateFormat(this.props.dateFormat);
    }
  }

  calculateChartData() {
    if (!this.props.data.length) {
      return false;
    }
    const data = this.generateData(this.props.data);
    const total = _(this.props.data)
      .flatMap('data')
      .reduce((t, d) => {
        return t + d.y;
      }, 0);
    this.setState({
      total
    });

    this.chart.update({
      series: [
        chartUtils.trendLineSeries({ data: data, color: chartUtils.TREND_LINE_COLOR }, true)
      ],
      xAxis: {
        plotLines: [
          {
            color: '#F5F5F5',
            width: 1,
            value: data[0].x,
            label: {
              text: ''
            }
          }
        ]
      }
    });
  }

  handleClose = () => {
    this.props.close();
    this.props.saveConfig();
  };

  generateData = data => {
    return _(data)
      .flatMap('data')
      .sortBy('x')
      .groupBy(i => moment(i.x).toISOString())
      .toPairs()
      .map(i => {
        let total = _.sumBy(i[1], 'y');

        return {
          x: +moment(i[0]),
          y: total
        };
      })
      .value();
  };

  updateDateFormat(dateFormat) {
    this.chart.update({
      xAxis: {
        dateTimeLabelFormats: dateUtils.createHighchartFormats(dateFormat)
      }
    });
  }

  updateFontSize = () => {
    this.chart.update({
      exporting: {
        enabled: this.state.isFullSreen
      },
      yAxis: {
        gridLineWidth: +this.state.isFullSreen,
        labels: {
          enabled: this.state.isFullSreen
        }
      }
    });

    this.chart.reflow();
  };

  handleDrag = (e, data) => {
    this.setState({ pos: { x: data.x, y: data.y } });
  };

  handleResize = () => {
    if (this.state.isFullSreen) {
      this.setState(
        {
          width: undefined,
          height: undefined,
          left: undefined,
          isFullSreen: false,
          pos: DEFAULT_POS
        },
        () => this.updateFontSize()
      );
    } else {
      this.setState(
        {
          width: '100%',
          height: '100%',
          left: -10,
          isFullSreen: true,
          pos: { x: 0, y: 0 }
        },
        () => this.updateFontSize()
      );
    }
  };

  render() {
    return (
      <Draggable position={this.state.pos} onStop={this.handleDrag} axis="both" bounds="parent">
        <div
          className={`box_white_shadow chart_averages_box ${this.state.isFullSreen ? 'fullscreen' : ''}`}
          style={{ width: this.state.width, height: this.state.height }}
        >
          <div className='box_white_title mb-4'>
            <h3>Throughput</h3>
            {/* the card's number on its title's line, so the chart keeps the card's height */}
            <p className="card-title-value">
              {this.state.total} item{this.state.total != 1 ? 's' : ''}
            </p>
            <div className='right-actions'>
              <Button variant='secondary' size='sm'
                className={`btn-action ${this.state.isFullSreen ? 'fullscreen' : ''}`}
                onClick={this.handleResize}
              >
                <img src={this.state.isFullSreen ? expandIcon : collapseIcon} alt="toggleFullSreen" />
              </Button>
              {/* in focus the chart can be downloaded, from the header like the card's other actions */}
              {this.state.isFullSreen && <ChartDownloadMenu getChart={() => this.chart} />}
              <Button className="btn-action" onClick={this.handleClose}>
                &times;
              </Button>
            </div>
          </div>

          <div ref={this.chartContainer} />
        </div>
      </Draggable>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    close: () => dispatch(updateLineChart(false, 'trc')),
    saveConfig: () => dispatch(saveConfig())
  };
};

export default withChartExport(connect(undefined, mapDispatchToProps)(ExactThroughput));
